import castArray from 'lodash/castArray';
import {ModelField, DocumentField, ValidationResult, ValueResolver, Option} from '@/engine';

import {ModelInputField as ModelInputField, ModelUIField as ModelUIField} from '@/engine/types/model/field';
import {FIELD_CATEGORIES, Validation, DomainValueReference, TemplateValue} from '@/model/types';
import {getFieldCategoryType} from '@/model/utils/fields';
import {DocumentProcessorUtils} from './utils';

const nonEmptyValidation = (value?: string | string[]): ValidationResult<never> => {
  if (Array.isArray(value)) {
    return value.length > 0 ? {valid: true} : {valid: false, error: 'required'};
  }

  return (value ?? '').trim().length > 0 ? {valid: true} : {valid: false, error: 'required'};
};

const validOptionValidation =
  (options: (Option | Option['value'])[], required: boolean, valueResolver: ValueResolver) =>
  (value?: string | string[]): ValidationResult<never> => {
    if (value === undefined || value === null || value === '') {
      return required ? {valid: false, error: 'required'} : {valid: true};
    }

    const allValid = castArray(value).every(v =>
      options.find(o => {
        const value = typeof o === 'object' && 'label' in o ? o.value : o;
        return valueResolver(value) === valueResolver(v);
      }),
    );

    if (allValid) {
      return {valid: true};
    }

    return {valid: false, error: 'invalid'};
  };

const nonRequiredValidation =
  <T extends (value?: string) => ValidationResult<Validation>>(validation: T) =>
  (value = ''): ValidationResult<Validation> =>
    value ? validation(value) : {valid: true};

const computeBaseField = (field: ModelField, utils: DocumentProcessorUtils, groupId: string) => {
  const {valueResolver, computeConditions, computeEvents, visibilitySet} = utils;

  const isGroupVisible = visibilitySet.has(groupId);
  const visible = isGroupVisible ? computeConditions(field.displayIf) : false;

  if (visible) {
    visibilitySet.add(field.id);
  } else {
    visibilitySet.delete(field.id);
  }

  return {
    id: field.id,
    label: field.label(valueResolver, visibilitySet),
    layout: field.layout,
    helper: field.helper(valueResolver, visibilitySet),
    visible,
    onMount: computeEvents(field.onMount),
  };
};

const computeInputField =
  (utils: DocumentProcessorUtils, groupId: string) =>
  (field: ModelInputField): DocumentField => {
    const {computeValidations, computeConditions, computeEvents, valueResolver, visibilitySet} = utils;

    const required = !field.requiredIf.length ? false : computeConditions(field.requiredIf);
    const readonly = !field.readonlyIf.length ? false : computeConditions(field.readonlyIf);
    const disabled = !field.disabledIf.length ? false : computeConditions(field.disabledIf);
    const validations = computeValidations(field.validations(valueResolver, visibilitySet));

    const conditionalValidations = required
      ? [nonEmptyValidation, ...validations]
      : validations.map(nonRequiredValidation);

    if (field.type === 'select' || field.type === 'combobox' || (field.type === 'checkbox' && !!field.multiple)) {
      const options = field.options(valueResolver, visibilitySet);

      conditionalValidations.unshift(validOptionValidation(options, required, valueResolver));
    }

    return {
      ...computeBaseField(field, utils, groupId),
      button: field.button,
      disabled,
      format: field.format,
      initialValue: field.initialValue(valueResolver, visibilitySet) as
        | string
        | DomainValueReference
        | TemplateValue
        | (string | DomainValueReference | TemplateValue)[]
        | undefined,
      mask: field.mask(valueResolver, visibilitySet),
      maxLength: field.maxLength(valueResolver, visibilitySet),
      maxValue: field.maxValue,
      metadata: field.metadata,
      minValue: field.minValue,
      multiple: field.multiple,
      onBlur: computeEvents(field.onBlur),
      onChange: computeEvents(field.onChange),
      onClick: computeEvents(field.onClick),
      onFocus: computeEvents(field.onFocus),
      options: field.options(valueResolver, visibilitySet),
      placeholder: field.placeholder,
      raw: field.raw,
      readonly,
      required,
      type: field.type,
      uppercasedMask: field.uppercasedMask,
      validations: conditionalValidations,
      value: field.value(valueResolver, visibilitySet),
    };
  };

const computeTableField = (field: ModelUIField, utils: DocumentProcessorUtils) => {
  const {store, valueResolver, visibilitySet} = utils;

  return {
    table: field.table
      ? {
          array: field.table.array,
          theme: field.table.theme,
          headers: field.table.headers.map(header => header(valueResolver, visibilitySet)),
          rows: field.table.rows(store, valueResolver, visibilitySet),
        }
      : undefined,
  };
};

const computeUIField =
  (utils: DocumentProcessorUtils, groupId: string) =>
  (field: ModelUIField): DocumentField => {
    return {
      ...computeBaseField(field, utils, groupId),
      ...computeTableField(field, utils),
      type: field.type,
    };
  };

export const computeField =
  (utils: DocumentProcessorUtils, groupId: string) =>
  (field: ModelField): DocumentField => {
    switch (getFieldCategoryType(field)) {
      case FIELD_CATEGORIES.INPUT:
        return computeInputField(utils, groupId)(field as ModelInputField);
      default:
        return computeUIField(utils, groupId)(field as ModelUIField);
    }
  };

export const _private = {
  nonEmptyValidation,
  validOptionValidation,
};
